
import { useContractChanges } from "@/hooks/useContractChanges";
import { TimelineEntry } from "./TimelineEntry";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { DollarSign } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useEffect } from "react";

interface ContractTimelineProps {
  contractId: string;
}

export function ContractTimeline({ contractId }: ContractTimelineProps) {
  const { t } = useTranslation();
  const { useContractChangesQuery } = useContractChanges();
  const { data: changes, isLoading, error } = useContractChangesQuery(contractId);

  useEffect(() => {
    if (changes) {
      console.log("Timeline received changes:", changes);
      
      // Check if we have currency information in any changes
      const hasCurrencyInfo = changes.some(change => {
        // Check in metadata
        if (change.metadata && typeof change.metadata === 'object' && 'currencyId' in change.metadata) {
          return true;
        }
        // Check in new_value for create type
        if (change.change_type === 'create' && 
            change.new_value && 
            typeof change.new_value === 'object' && 
            'currencyId' in change.new_value) {
          return true;
        }
        return false;
      });
      
      console.log("Timeline has currency information:", hasCurrencyInfo);
      
      // Check which changes are cost-related
      const costChanges = changes.filter(change => 
        change.field_name === 'expectedCost' || change.change_type === 'create'
      );
      
      console.log("Cost-related changes:", costChanges.length);
      console.log("Deduplicated changes total:", changes.length);
    }
  }, [changes]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full animate-pulse" />
        ))}
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {t('timeline.error', 'Error loading cost history data')}
        </AlertDescription>
      </Alert>
    );
  }

  if (!changes?.length) {
    return (
      <div className="text-center py-8 text-muted-foreground animate-fade-in">
        <div className="flex flex-col items-center gap-2">
          <DollarSign className="h-10 w-10 text-muted-foreground/50" />
          <p>{t('timeline.noChanges', 'No cost changes recorded for this contract yet.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {changes.map((change, index) => (
        <div 
          key={change.id}
          className="transition-all duration-300"
          style={{ 
            animationDelay: `${index * 100}ms`,
            opacity: 0,
            animation: 'fade-in 0.5s ease-out forwards'
          }}
        >
          <TimelineEntry change={change} />
        </div>
      ))}
    </div>
  );
}
